import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const { login, signup } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/dashboard";
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = isLogin
        ? await login(form.email, form.password)
        : await signup(form.name, form.email, form.password);
      toast.success(isLogin ? "Welcome back" : "Account created");
      navigate(user.role === "admin" ? "/admin" : next);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    } finally { setBusy(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="max-w-md mx-auto px-5 py-28" data-testid={isLogin ? "login-page" : "signup-page"}>
      <div className="hy-label text-center">{isLogin ? "Sign in" : "Create account"}</div>
      <h1 className="font-display text-3xl sm:text-4xl text-white mt-5 text-center">
        {isLogin ? "Welcome back" : "Join the registry"}
      </h1>

      <form onSubmit={submit} className="hy-card p-8 mt-12 space-y-6" data-testid="auth-form">
        {!isLogin && (
          <div>
            <div className="hy-label mb-3">Full Name</div>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="hy-input" data-testid="auth-name" />
          </div>
        )}
        <div>
          <div className="hy-label mb-3">Email</div>
          <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="hy-input" data-testid="auth-email" />
        </div>
        <div>
          <div className="hy-label mb-3">Password</div>
          <input required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="hy-input" data-testid="auth-password" />
        </div>
        <button disabled={busy} className="hy-btn w-full py-4 text-xs uppercase flex items-center justify-center gap-2 disabled:opacity-60" data-testid="auth-submit">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} {isLogin ? "Sign in" : "Create account"}
        </button>

        <div className="flex items-center gap-4">
          <div className="hy-rule flex-1" />
          <span className="text-xs text-slate-500 uppercase tracking-widest">or</span>
          <div className="hy-rule flex-1" />
        </div>

        <button type="button" onClick={google} className="hy-btn-ghost w-full py-4 text-xs uppercase" data-testid="google-auth-btn">
          Continue with Google
        </button>

        <p className="text-center text-xs text-slate-500">
          {isLogin ? (
            <>No account? <Link to="/signup" className="text-amber-300 underline" data-testid="to-signup">Create one</Link></>
          ) : (
            <>Already registered? <Link to="/login" className="text-amber-300 underline" data-testid="to-login">Sign in</Link></>
          )}
        </p>
      </form>
    </div>
  );
}
